import { useEffect, useRef, useState } from 'react';
import {
  getMemberTriages,
  TriageModel,
  TriageRecipient,
} from 'data/api/triage.api';
import { isForbidden } from './components/EventDetails';

export interface MemberTriages {
  triages: TriageModel[];
  loading: boolean;
  error: string;
  /** A 403 cannot come good however many times it is asked. */
  retryable: boolean;
  /** Refetch — called after every mutation, and by the error state's retry. */
  reload: () => void;
  /**
   * Background re-read for polling: no loading state, errors keep what is on
   * screen, and the list only changes when the server's answer did.
   */
  refresh: () => void;
  /**
   * Records recipients the server has already accepted, so the list is right
   * before — and even without — the refetch that follows.
   */
  commitRecipients: (triagesId: string, recipients: TriageRecipient[]) => void;
}

// The list carries the member it was fetched for. Clearing on a member change
// inside an effect is too late: the page re-renders under the newly selected
// family before effects run, so the previous family's triage subjects — and
// the inline events in their thread — would be shown once. Ownership is
// checked at read time instead. Same shape as ConversationSummary and
// EventDetails, and this is the same class of surface: subjects and note
// snippets about one family's care in front of a rep reading another's.
interface TriagesState {
  owner?: string;
  triages: TriageModel[];
  loading: boolean;
  error: string;
  retryable: boolean;
}

const EMPTY: TriagesState = {
  owner: undefined,
  triages: [],
  loading: false,
  error: '',
  retryable: false,
};

/**
 * One triage read per selected member, shared by the context rail and the
 * inline thread events. Two consumers, one request.
 */
export const useMemberTriages = (userId?: string): MemberTriages => {
  const [state, setState] = useState<TriagesState>(EMPTY);
  const [attempt, setAttempt] = useState<number>(0);
  // Bumped by every load and refresh, so only the latest one may land.
  const seqRef = useRef(0);

  useEffect(() => {
    seqRef.current += 1;
    if (!userId) {
      setState(EMPTY);
      return undefined;
    }
    let live = true;
    // A reload for the same member keeps the rows on screen while it runs —
    // refetching after sending a triage must not blank the list the rep just
    // watched appear. A different member starts from nothing.
    setState((current) =>
      current.owner === userId
        ? { ...current, loading: true }
        : { ...EMPTY, owner: userId, loading: true },
    );
    getMemberTriages(userId)
      .then((triages) => {
        if (!live) return;
        setState({ ...EMPTY, owner: userId, triages });
      })
      .catch((error) => {
        if (!live) return;
        const forbidden = isForbidden(error);
        // A failed refresh after a send keeps what is on screen: dropping it
        // would close the triage the rep just replied on, as if the reply had
        // failed. A 403 is different — nothing of this family's stays up.
        setState((current) => ({
          ...EMPTY,
          owner: userId,
          triages:
            !forbidden && current.owner === userId ? current.triages : [],
          error: forbidden
            ? 'You do not have permission to see this family’s triages.'
            : 'Could not load triages.',
          retryable: !forbidden,
        }));
      });
    return () => {
      live = false;
    };
  }, [userId, attempt]);

  const refresh = () => {
    if (!userId || state.owner !== userId || state.loading || state.error) {
      return;
    }
    const seq = ++seqRef.current;
    getMemberTriages(userId)
      .then((triages) => {
        if (seq !== seqRef.current) return;
        setState((current) =>
          current.owner === userId &&
          JSON.stringify(current.triages) !== JSON.stringify(triages)
            ? { ...current, triages }
            : current,
        );
      })
      .catch(() => undefined);
  };

  const owned = state.owner === userId ? state : null;

  return {
    triages: owned?.triages || [],
    loading: !!userId && (!owned || owned.loading),
    error: owned?.error || '',
    retryable: !!owned?.retryable,
    reload: () => setAttempt((current) => current + 1),
    refresh,
    commitRecipients: (triagesId, recipients) =>
      setState((current) =>
        current.owner === userId
          ? {
              ...current,
              triages: current.triages.map((triage) =>
                triage.triagesId === triagesId
                  ? { ...triage, recipients }
                  : triage,
              ),
            }
          : current,
      ),
  };
};
